'use client';

/**
 * AI-Usage Mini-Widget (Header oben rechts).
 *
 * Zwei per-Kategorie-Indikatoren: Developer (blau) + Enduser (violett), je mit
 * Plan-Name, Kurz-Badge, Verbrauchs-Balken und Prozent. Pro Kategorie eine
 * "Limit erreicht → Wallet-Nutzung aktivieren"-Aktion (admin-only) bzw.
 * "Wallet wird belastet ✓" wenn eingewilligt. Dazu die Wallet-Balance.
 *
 * Backend-Contract (siehe Plan when-quota-ends-user-shiny-flask.md §2):
 *   GET  /api/ai-usage/quota-status  → per-Kategorie ODER legacy-flat (normalizeStatus)
 *   POST /api/ai-usage/wallet-consent {category, enabled} → {success} (admin-only, 403 sonst)
 *   GET  /api/billing/wallet/ledger?page=1&page_size=5  (beim Öffnen, Wallet-Box: letzte Transaktionen)
 *
 * Polling: 60s quota-status.
 * Alle Texte i18n unter ai_usage.* (DE+EN, namespace default).
 *
 * Owner-Agent: ai-usage-tracking-agent
 * Rule: .claude/rule-details/ai-usage-tracking.md
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { Sparkles, AlertTriangle, Wallet, Check } from 'lucide-react';
import { BackendAuth } from '@/lib/auth-backend';
import { useTranslation } from '@/hooks/useTranslation';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';

const POLL_INTERVAL_MS = 60_000;

const eurFmt = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// NOTE: token COUNTS are formatted locale-aware (per active UI language) via a
// useMemo'd Intl.NumberFormat built inside the component — EN → "283,376",
// DE → "283.376". The EUR formatter (eurFmt) intentionally stays de-DE
// (rule ai-usage-tracking.md:80). No module-level count formatter.

// Customer-facing plan label. The internal plan_name keeps the ladder/brand
// prefix ("XOFFI Developer WEBSITE", "Enduser Basic") because the resolver
// matches on it — but the widget already color-codes dev vs enduser, so the
// ladder word is redundant here. Strip "XOFFI/Developer/Enduser" and title-case
// the tier: "XOFFI Developer WEBSITE" → "Website", "Enduser Basic" → "Free".
//
// The €0 tier is internally named "Basic" in BOTH ladders (plan_code
// dev_basic / enduser_basic, price_monthly 0.00). There is NO paid "Basic"
// tier — the paid tiers are Website/Starter/Pro/Enterprise — so the label
// "Basic" is unambiguously the free tier and is shown to customers as "Free"
// (proper-noun label, consistent with the other untranslated tier names).
function cleanPlanName(name) {
  if (!name) return name;
  const stripped = String(name)
    .replace(/^XOFFI\s+/i, '')
    .replace(/^(Developer|Enduser)\s+/i, '')
    .trim();
  const titled = stripped.replace(/\S+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
  if (titled === 'Basic') return 'Free';
  return titled;
}

/**
 * Normalisiert die quota-status-Antwort auf eine einheitliche Form.
 * Unterstützt BEIDE Shapes:
 *   - NEU (per-Kategorie): { developer:{...}, enduser:{...}, wallet:{...}, is_admin, ... }
 *   - LEGACY (flat):       { used, limit, percent, is_blocked, plan_name, ... }
 * Legacy → EIN neutraler Indikator (category='neutral'), kein Wallet-Fallback.
 */
function normalizeCat(raw, key) {
  const c = raw?.[key];
  if (!c || typeof c !== 'object') return null;
  const used = Number(c.used) || 0;
  const limit = Number(c.limit) || 0;
  const percent =
    typeof c.percent === 'number'
      ? c.percent
      : limit > 0
        ? Math.min(100, (used / limit) * 100)
        : 0;
  // Overflow = tokens spent ABOVE the plan limit. These are ALWAYS billed from
  // the wallet (ai_wallet_tailer), regardless of consent — so we surface the raw
  // over-limit amount as an explicit token count. Prefer the backend field
  // (overflow_tokens); fall back to the raw used−limit delta for older backends.
  const overflowTokens =
    typeof c.overflow_tokens === 'number'
      ? Math.max(0, c.overflow_tokens)
      : limit > 0
        ? Math.max(0, used - limit)
        : 0;
  return {
    key,
    // Plan usage is CAPPED at the plan limit: tokens spent above the limit are
    // billed from the wallet (ai_wallet_tailer), NOT from the plan — so they must
    // not inflate the plan bar past 100%. The raw over-limit total stays in the
    // audit counter (xoffi_tokens_used_this_period); the widget only shows the
    // plan-funded portion. Wallet spend is reflected by the wallet balance.
    used: limit > 0 ? Math.min(used, limit) : used,
    limit,
    overflowTokens,
    percent: Math.min(100, percent),
    isBlocked: !!c.is_blocked,
    walletFallbackActive: !!c.wallet_fallback_active,
    consented: !!c.consented,
    planName: c.plan_name || null,
    planShort: c.plan_short || null,
    unlimited: limit === 0,
  };
}

function normalizeStatus(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const hasPerCategory =
    (raw.developer && typeof raw.developer === 'object') ||
    (raw.enduser && typeof raw.enduser === 'object');

  const walletCents =
    Number(raw?.wallet?.available_cents ?? raw?.wallet_available_cents ?? 0) || 0;
  const walletCurrency = raw?.wallet?.currency || 'EUR';

  const user = BackendAuth.getUser();
  const derivedAdmin = !!(
    user &&
    (user.is_admin === true ||
      user.is_admin === 1 ||
      user.nexus_access === true ||
      user.nexus_core_access === true)
  );
  const isAdmin = raw.is_admin === true || raw.is_admin === 1 || derivedAdmin;

  if (hasPerCategory) {
    const categories = [
      normalizeCat(raw, 'developer'),
      normalizeCat(raw, 'enduser'),
    ].filter(Boolean);
    return {
      shape: 'per_category',
      categories,
      walletCents,
      walletCurrency,
      isAdmin,
      enforcementOn: raw.enforcement_on !== false,
      periodEnd: raw.period_end ? new Date(raw.period_end) : null,
    };
  }

  // LEGACY flat → ein neutraler Indikator
  const used = Number(raw.used) || 0;
  const limit = Number(raw.limit) || 0;
  const percent =
    typeof raw.percent === 'number'
      ? raw.percent
      : limit > 0
        ? Math.min(100, (used / limit) * 100)
        : 0;
  return {
    shape: 'legacy',
    categories: [
      {
        key: 'neutral',
        // Cap plan usage at the limit (wallet-billed overage ≠ plan usage).
        used: limit > 0 ? Math.min(used, limit) : used,
        limit,
        overflowTokens:
          typeof raw.overflow_tokens === 'number'
            ? Math.max(0, raw.overflow_tokens)
            : limit > 0
              ? Math.max(0, used - limit)
              : 0,
        percent: Math.min(100, percent),
        isBlocked: !!raw.is_blocked,
        walletFallbackActive: false,
        consented: false,
        planName: raw.plan_name || null,
        planShort: null,
        unlimited: limit === 0,
      },
    ],
    walletCents,
    walletCurrency,
    isAdmin,
    enforcementOn: raw.enforcement_on !== false,
    periodEnd: raw.period_end ? new Date(raw.period_end) : null,
  };
}

// Farb-Design pro Kategorie (matcht QuotaExceededDialog): dev=blau, enduser=violett.
const CAT_THEME = {
  developer: {
    label: 'ai_usage.developer',
    ladderLabel: 'ai_usage.dev_ladder',
    text: 'text-blue-700 dark:text-blue-300',
    badge:
      'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300',
    border: 'border-blue-200 dark:border-blue-500/30',
    bg: 'bg-blue-50 dark:bg-blue-500/10',
    bar: 'bg-blue-500',
  },
  enduser: {
    label: 'ai_usage.enduser',
    ladderLabel: 'ai_usage.enduser_ladder',
    text: 'text-violet-700 dark:text-violet-300',
    badge:
      'bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300',
    border: 'border-violet-200 dark:border-violet-500/30',
    bg: 'bg-violet-50 dark:bg-violet-500/10',
    bar: 'bg-violet-500',
  },
  neutral: {
    label: 'ai_usage.title',
    text: 'text-gray-700 dark:text-gray-200',
    badge: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
    border: 'border-gray-200 dark:border-gray-700',
    bg: 'bg-gray-50 dark:bg-gray-800/40',
    bar: 'bg-gray-500',
  },
};

// Balken-Farbe nach Schweregrad (green/orange/red) — überschreibt die Kategorie-Farbe.
function severityBar(percent, isBlocked, catBar) {
  if (isBlocked || percent >= 90) return 'bg-red-500';
  if (percent >= 80) return 'bg-orange-500';
  return catBar; // < 80% → Kategorie-Farbe (blau/violett)
}

// Trigger-Icon-Farbe / Ring aus dem "schlimmsten" Zustand aller Kategorien.
function triggerState(categories) {
  const anyBlocked = categories.some((c) => c.isBlocked);
  const bounded = categories.filter((c) => !c.unlimited);
  const maxPercent = bounded.length
    ? Math.max(...bounded.map((c) => c.percent))
    : 0;
  const allUnlimited = bounded.length === 0;

  if (anyBlocked) {
    return {
      level: 'blocked',
      icon: 'text-red-500',
      ring: 'ring-red-500',
      badge: 'bg-red-500 text-white animate-pulse',
      maxPercent,
      allUnlimited,
    };
  }
  if (maxPercent >= 90) {
    return {
      level: 'critical',
      icon: 'text-red-500',
      ring: 'ring-red-500/50',
      badge: 'bg-red-500/90 text-white',
      maxPercent,
      allUnlimited,
    };
  }
  if (maxPercent >= 80) {
    return {
      level: 'warn',
      icon: 'text-orange-500',
      ring: 'ring-orange-500/50',
      badge: 'bg-orange-500 text-white',
      maxPercent,
      allUnlimited,
    };
  }
  return {
    level: 'ok',
    icon: 'text-gray-500 dark:text-gray-400',
    ring: '',
    badge: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200',
    maxPercent,
    allUnlimited,
  };
}

// Cross-surface consent sync: dispatched on ANY successful wallet-consent change
// so every mounted consent surface (this header widget, the wallet-page card)
// re-fetches immediately — no page refresh needed. Mirrors the existing
// window-CustomEvent pattern (xoffi:user-data-updated).
export const AI_CONSENT_EVENT = 'xoffi:ai-wallet-consent-updated';

/**
 * Regel (20.10.2026): KI-Verbrauch ist in ALLEN Layouts nur fuer Admins sichtbar.
 * Gleiche Admin-Erkennung wie im demo1-Header (is_admin / isAdmin / role).
 */
export function isAiUsageAdmin(user) {
  if (!user) return false;
  const raw = user.is_admin ?? user.isAdmin ?? user.role;
  if (raw === true) return true;
  if (typeof raw === 'number') return raw === 1;
  if (typeof raw === 'string') {
    const s = raw.toLowerCase();
    if (s === '1' || s === 'true' || s.includes('admin')) return true;
  }
  const role = String(user.role || '').toLowerCase();
  return role === 'admin' || role === 'super_admin';
}

export default function AiUsageWidget() {
  // Gate VOR dem Mount der Polling-Komponente: Nicht-Admins pollen gar nicht erst.
  const [allowed, setAllowed] = useState(false);
  useEffect(() => {
    setAllowed(isAiUsageAdmin(BackendAuth.getUser()));
  }, []);
  if (!allowed) return null;
  return <AiUsageWidgetInner />;
}

function AiUsageWidgetInner() {
  const { t, i18n } = useTranslation();
  // Token counts follow the active UI language (EN → comma, DE → dot).
  const intFmt = useMemo(
    () => new Intl.NumberFormat(i18n?.language || 'en'),
    [i18n?.language],
  );
  const [raw, setRaw] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [ledger, setLedger] = useState([]); // last wallet transactions (on open)
  const [consentBusy, setConsentBusy] = useState(null); // category being enabled
  const [consentError, setConsentError] = useState(null);
  const intervalRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const data = await BackendAuth.apiCallJson('/api/ai-usage/quota-status');
      setRaw(data);
      setError(null);
    } catch (e) {
      setError(e?.message || 'load_failed');
    }
  };

  // Letzte Wallet-Transaktionen (beim Öffnen) — für die Wallet-Box im Popover.
  const fetchLedger = async () => {
    try {
      const data = await BackendAuth.apiCallJson(
        '/api/billing/wallet/ledger?page=1&page_size=5',
      );
      setLedger(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      // non-fatal — Wallet-Box zeigt dann nur den Saldo
    }
  };

  useEffect(() => {
    fetchStatus();
    intervalRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (open) fetchLedger();
  }, [open]);

  // Live-Sync mit anderen Consent-Flächen (z.B. Wallet-Seite): re-fetch, sobald
  // dort die Wallet-Nutzung ein/ausgeschaltet wurde — ohne Page-Refresh.
  useEffect(() => {
    const onConsentUpdated = () => {
      fetchStatus();
    };
    window.addEventListener(AI_CONSENT_EVENT, onConsentUpdated);
    return () => window.removeEventListener(AI_CONSENT_EVENT, onConsentUpdated);
  }, []);

  // enabled=true → Wallet-Nutzung für die Kategorie aktivieren; false → widerrufen.
  // Beides admin-only (Endpoint = require_org_admin). Re-Fetch flippt die Karte.
  const handleConsent = async (category, enabled = true) => {
    setConsentBusy(category);
    setConsentError(null);
    try {
      await BackendAuth.apiCallJson('/api/ai-usage/wallet-consent', {
        method: 'POST',
        body: JSON.stringify({ category, enabled }),
      });
      await fetchStatus(); // re-fetch → Karte flippt (aktiviert ⇄ deaktiviert)
      // andere gemountete Consent-Flächen sofort synchronisieren (kein Refresh).
      try {
        window.dispatchEvent(new CustomEvent(AI_CONSENT_EVENT));
      } catch (_) {}
    } catch (e) {
      setConsentError(category);
    } finally {
      setConsentBusy(null);
    }
  };

  if (error) return null; // Keine UI bei Fehlern (z.B. ausgeloggt)

  const norm = normalizeStatus(raw);
  if (!norm || norm.categories.length === 0) return null;

  const trig = triggerState(norm.categories);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`h-9 px-2 gap-1.5 rounded-full hover:bg-primary/10 ${
            trig.level === 'blocked' || trig.maxPercent >= 90
              ? `ring-2 ${trig.ring}`
              : ''
          }`}
          title={
            trig.allUnlimited
              ? t('ai_usage.tooltip_unlimited', { editable: false })
              : t('ai_usage.tooltip', { editable: false })
          }
          aria-label={t('ai_usage.tooltip', { editable: false })}
        >
          {trig.level === 'blocked' ? (
            <AlertTriangle className="size-4 text-red-500 animate-pulse" />
          ) : (
            <Sparkles className={`size-4 ${trig.icon}`} />
          )}
          <span
            className={`text-xs font-semibold rounded-full px-1.5 py-0.5 ${trig.badge}`}
          >
            {/* exakter Wert (max. 2 Dezimalen, trailing zeros weg) — nie auf
                "100%" aufrunden, solange das Limit real nicht erreicht ist */}
            {trig.allUnlimited ? '∞' : `${+trig.maxPercent.toFixed(2)}%`}
          </span>
        </Button>
      </PopoverTrigger>
      <WidgetContent
        norm={norm}
        ledger={ledger}
        t={t}
        intFmt={intFmt}
        onConsent={handleConsent}
        consentBusy={consentBusy}
        consentError={consentError}
      />
    </Popover>
  );
}

function CategoryIndicator({ cat, t, intFmt, isAdmin, onConsent, consentBusy, consentError }) {
  const theme = CAT_THEME[cat.key] || CAT_THEME.neutral;
  const bar = severityBar(cat.percent, cat.isBlocked, theme.bar);
  const busy = consentBusy === cat.key;
  const failed = consentError === cat.key;

  return (
    <div className={`rounded-lg border ${theme.border} ${theme.bg} p-3`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1 min-w-0">
          {/* Label = the ladder ("Developer" / "End User"); badge = the plan tier
              ("Website" / "Basic" / …). Colour still distinguishes dev vs enduser. */}
          <span className={`text-sm font-semibold shrink-0 ${theme.text}`}>
            {theme.ladderLabel ? t(theme.ladderLabel) : (cleanPlanName(cat.planName) || t(theme.label))}
          </span>
          {theme.ladderLabel && cleanPlanName(cat.planName) && (
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold shrink-0 ${theme.badge}`}
            >
              {cleanPlanName(cat.planName)}
            </span>
          )}
        </div>
        <span className="text-xs font-medium tabular-nums text-foreground shrink-0">
          {/* exakter Wert (max. 2 Dezimalen) — 100% erst bei erreichtem Limit */}
          {cat.unlimited ? '∞' : `${+cat.percent.toFixed(2)}%`}
        </span>
      </div>

      {/* Muted subtitle = the AI use-case ("Building" / "Everyday") — this is
          distinct from the ladder label above, so it is NOT redundant. */}
      {cat.planName && (
        <div className="mt-0.5 text-[11px] text-muted-foreground truncate">
          {t(theme.label)}
        </div>
      )}

      {cat.unlimited ? (
        <div className="mt-2 text-xs text-muted-foreground">
          {t('ai_usage.no_limit')}
        </div>
      ) : (
        <>
          <div className="mt-2 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${bar}`}
              style={{ width: `${Math.min(100, cat.percent)}%` }}
            />
          </div>
          <div className="mt-1.5 flex justify-between text-xs text-muted-foreground tabular-nums">
            <span>{intFmt.format(cat.used)}</span>
            <span>
              {t('ai_usage.of')} {intFmt.format(cat.limit)}
            </span>
          </div>
          {/* Overflow over the plan limit — ALWAYS wallet-billed (ai_wallet_tailer),
              shown as an absolute token count so the over-limit amount is visible
              even though the bar/percent cap at 100%. */}
          {cat.overflowTokens > 0 && (
            <div className="mt-1.5 flex items-center gap-1 text-[11px] font-medium tabular-nums text-red-600 dark:text-red-400">
              <AlertTriangle size={12} aria-hidden="true" className="shrink-0" />
              <span>
                {t('ai_usage.over_limit_tokens', {
                  tokens: intFmt.format(cat.overflowTokens),
                  editable: false,
                })}
              </span>
            </div>
          )}
        </>
      )}

      {/* Per-Kategorie Block-Karte — nur wenn diese Kategorie geblockt ist. */}
      {cat.isBlocked && (
        <div className="mt-2">
          {cat.walletFallbackActive || cat.consented ? (
            <div className="space-y-1.5">
              <div className="flex items-start gap-1.5 rounded-md border border-green-200 bg-green-50 p-2 text-xs text-green-800 dark:border-green-500/30 dark:bg-green-500/10 dark:text-green-300">
                <Check size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
                <span>{t('ai_usage.wallet_in_use')}</span>
              </div>
              {/* Admin kann die Wallet-Nutzung für diese Kategorie wieder widerrufen. */}
              {isAdmin && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => onConsent(cat.key, false)}
                  className="h-auto w-full py-1 text-[11px] text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
                >
                  {busy
                    ? t('ai_usage.disabling', { editable: false })
                    : t('ai_usage.disable_wallet', { editable: false })}
                </Button>
              )}
            </div>
          ) : isAdmin ? (
            <>
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => onConsent(cat.key)}
                className="w-full gap-1.5"
              >
                <Wallet size={14} aria-hidden="true" />
                {busy
                  ? t('ai_usage.enabling', { editable: false })
                  : t('ai_usage.enable_wallet', { editable: false })}
              </Button>
              {failed && (
                <div className="mt-1 text-[11px] text-red-600 dark:text-red-400">
                  {t('ai_usage.consent_error')}
                </div>
              )}
            </>
          ) : (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
              {t('ai_usage.ask_admin')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function WidgetContent({ norm, ledger, t, intFmt, onConsent, consentBusy, consentError }) {
  const { categories, walletCents, walletCurrency, isAdmin, periodEnd } = norm;

  const walletFmt =
    walletCurrency === 'EUR'
      ? eurFmt
      : new Intl.NumberFormat('de-DE', {
          style: 'currency',
          currency: walletCurrency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

  return (
    <PopoverContent align="end" className="w-80 p-0 overflow-hidden">
      <div className="px-4 py-3 border-b bg-gray-50 dark:bg-gray-800/50">
        <div className="flex items-center gap-2">
          <Sparkles className="size-4 text-primary" />
          <span className="font-semibold text-sm">{t('ai_usage.title')}</span>
        </div>
      </div>

      <div className="px-4 py-3 space-y-2.5">
        {categories.map((cat) => (
          <CategoryIndicator
            key={cat.key}
            cat={cat}
            t={t}
            intFmt={intFmt}
            isAdmin={isAdmin}
            onConsent={onConsent}
            consentBusy={consentBusy}
            consentError={consentError}
          />
        ))}

        {/* Wallet-Box (Saldo + letzte 5 Transaktionen) — füllt den unteren Teil. */}
        <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
              <Wallet size={14} aria-hidden="true" />
              {t('ai_usage.wallet_title')}
            </span>
            <span className="font-semibold tabular-nums text-foreground">
              {walletFmt.format((walletCents || 0) / 100)}
            </span>
          </div>
          <div className="space-y-1.5 border-t border-border/60 pt-2">
            {!ledger || ledger.length === 0 ? (
              <div className="text-[11px] text-muted-foreground">
                {t('ai_usage.no_transactions', { editable: false })}
              </div>
            ) : (
              <>
              <div className="text-[11px] font-medium text-muted-foreground">
                {t('ai_usage.wallet_transactions_title', { editable: false })}
              </div>
              {ledger.slice(0, 5).map((row, i) => {
                const amt = Number(row.amount_cents) || 0;
                let when = '';
                try {
                  when = row.created_at
                    ? new Date(row.created_at).toLocaleDateString('de-DE')
                    : '';
                } catch (_) {
                  when = '';
                }
                return (
                  <div
                    key={row.id ?? i}
                    className="flex items-center justify-between gap-2"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-[11px] text-foreground">
                        {/* ai_usage-Debits: DB-Description ist hart deutsch (ai_wallet_tailer)
                            → display-seitig übersetzen (DE+EN) */}
                        {row.reference_type === 'ai_usage'
                          ? t('billing.wallet.desc_ai_overage', { editable: false })
                          : row.description || row.entry_type || '—'}
                      </div>
                      {when && (
                        <div className="text-[10px] text-muted-foreground">
                          {when}
                        </div>
                      )}
                    </div>
                    <span
                      className={`shrink-0 text-[11px] font-medium tabular-nums ${
                        amt >= 0
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-destructive'
                      }`}
                    >
                      {(amt >= 0 ? '+' : '') + walletFmt.format(amt / 100)}
                    </span>
                  </div>
                );
              })}
              </>
            )}
          </div>
        </div>

        {/* Reset-Zeile UNTER allen Boxen — deutlich sichtbar (eigene Box). */}
        {periodEnd && (
          <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-center text-xs font-medium text-foreground">
            {t('ai_usage.plans_reset_on', {
              date: periodEnd.toLocaleDateString('de-DE'),
              editable: false,
            })}
          </div>
        )}
      </div>

      <div className="px-4 py-2 border-t bg-gray-50 dark:bg-gray-800/50">
        <Link
          href="/admin/company?tab=ai-usage"
          className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
        >
          {t('ai_usage.details_link')}
        </Link>
      </div>
    </PopoverContent>
  );
}
